/**
 * The decisions `VideoFeed` makes, as plain functions so they can be tested
 * without a browser: which slide plays, which ones load, when to ask for the
 * next page, and what a key or a failed `play()` means.
 */

/**
 * How many slides short of the end the next page is asked for. Three is about
 * one page's worth of watching at a few seconds a video, which is longer than
 * a page takes to sign and return, so a steady swipe never lands on the
 * loader.
 */
export const END_REACHED_THRESHOLD = 3;

/**
 * The IntersectionObserver thresholds. Quarter steps are enough to tell which
 * of two slides holds most of the screen mid-swipe without a callback on
 * every pixel.
 */
export const VISIBILITY_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];

/**
 * The slide that should play: the one showing most of itself. The current
 * slide wins a tie, so two slides half on screen do not trade playback back
 * and forth; with nothing visible at all the current slide holds.
 */
export function pickActiveIndex(
  ratios: ReadonlyMap<number, number>,
  current: number,
): number {
  let best = current;
  let bestRatio = ratios.get(current) ?? 0;
  for (const [index, ratio] of ratios) {
    if (ratio > bestRatio) {
      best = index;
      bestRatio = ratio;
    }
  }
  return bestRatio > 0 ? best : current;
}

/**
 * Whether a slide holds a video element and its file now: the one playing
 * and one either side, so the next swipe lands on a video that has already
 * started loading. Every other slide holds only its poster.
 */
export function isWarm(index: number, active: number): boolean {
  return Math.abs(index - active) <= 1;
}

/**
 * How many video elements the feed plays on. Three is the warm window: the
 * current slide and one either side.
 */
export const POOL_SIZE = 3;

/**
 * Which pooled element a slide plays on. Any three consecutive slides land on
 * three different elements, so the warm window never asks for one twice, and
 * a swipe hands the element leaving the window to the slide entering it.
 */
export function poolSlot(index: number): number {
  return index % POOL_SIZE;
}

/**
 * Whether to ask for the next page: the viewer is within `threshold` slides of
 * the last one, there is more, and nothing is already on its way. `active`
 * may be the loader's own index, one past the last video.
 */
export function shouldLoadMore({
  active,
  count,
  hasMore,
  loading,
  threshold = END_REACHED_THRESHOLD,
}: {
  active: number;
  count: number;
  hasMore: boolean;
  loading: boolean;
  threshold?: number;
}): boolean {
  if (!hasMore || loading || count === 0) return false;
  return count - 1 - active <= threshold;
}

/** One step through the feed, held at either end. `stops` counts the loader. */
export function stepIndex(
  current: number,
  delta: 1 | -1,
  stops: number,
): number {
  return Math.min(Math.max(current + delta, 0), Math.max(stops - 1, 0));
}

export type FeedKeyAction = "next" | "previous" | "toggle";

/**
 * The feed's keyboard. j and k are the feed-reader convention (and what
 * YouTube Shorts uses); the arrows and page keys are what a scroller already
 * answers to, taken over so a press moves exactly one video. Escape is the
 * dialog's, not this.
 */
export function feedKeyAction(key: string): FeedKeyAction | null {
  switch (key) {
    case "ArrowDown":
    case "PageDown":
    case "j":
      return "next";
    case "ArrowUp":
    case "PageUp":
    case "k":
      return "previous";
    case " ":
      return "toggle";
    default:
      return null;
  }
}

export type PlayFailure = "blocked" | "interrupted" | "failed";

/**
 * What a rejected `play()` means, from the `DOMException`'s name.
 *
 * `NotAllowedError` is the browser's autoplay policy refusing sound without a
 * gesture, which the feed answers by playing muted. `AbortError` is a `pause()`
 * or a new source arriving before playback started, which is the normal
 * result of swiping past a video that was still loading. Anything else is the
 * file itself.
 */
export function classifyPlayFailure(name: string | undefined): PlayFailure {
  if (name === "NotAllowedError") return "blocked";
  if (name === "AbortError") return "interrupted";
  return "failed";
}

/** How far through a video is, 0 to 1, safe on a file still loading. */
export function progressFraction(
  currentTime: number,
  duration: number,
): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return Math.min(Math.max(currentTime / duration, 0), 1);
}

/**
 * A list with each id once, first occurrence kept. Pages of a ranking read
 * from a cache that can refresh between them, so the same ad can come back
 * on two pages; as a React key that is a collision.
 */
export function uniqueById<T extends { id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}
